import { OrganizationCode } from '@quromedical/fhir-common';
import { AdministratorSchema } from '@quromedical/healthbridge/client';
import { z } from 'zod';
import { UUIDSchema } from '../domain/models';
import { PaginationSchema } from '../domain/pagination';
import { PaginatedBundleSchema } from '../domain/schema';
import {
  ContactGetSchema,
  ContactSchema,
  type ContactUpdateSchema,
} from '../person/schema';

const OrganizationTypeSchema = z.nativeEnum(OrganizationCode);

export const OrganizationGeneralBaseSchema = z.object({
  name: z.string(),
  type: OrganizationTypeSchema,
  schemeCode: z.string().optional(),
});
export type OrganizationGeneralBase = z.infer<
  typeof OrganizationGeneralBaseSchema
>;

export const OrganizationGeneralSchema = OrganizationGeneralBaseSchema;
export type OrganizationGeneral = z.infer<typeof OrganizationGeneralSchema>;

export const OrganizationGeneralUpdateSchema =
  OrganizationGeneralBaseSchema.extend({
    id: UUIDSchema,
    schemeCode: z.string().optional(),
  });

export const OrganizationSchema = z.object({
  contact: ContactSchema,
  general: OrganizationGeneralSchema,
});
export type Organization = z.infer<typeof OrganizationSchema>;

export const PartialOrganizationSchema = z.object({
  contact: ContactGetSchema.partial(),
  general: OrganizationGeneralBaseSchema.partial(),
});
export type PartialOrganization = z.infer<typeof PartialOrganizationSchema>;

export type OrganizationContactUpdateRequest = z.infer<
  typeof ContactUpdateSchema
>;
export type OrganizationContactUpdateResponse = OrganizationGetResponse;

export type OrganizationGeneralUpdateRequest = z.infer<
  typeof OrganizationGeneralUpdateSchema
>;
export type OrganizationGeneralUpdateResponse = OrganizationGetResponse;

export const InsuranceOrganizationSchema = AdministratorSchema.extend({
  id: UUIDSchema,
});
export type InsuranceOrganization = z.infer<typeof InsuranceOrganizationSchema>;

export const OrganizationGetResponseSchema = PartialOrganizationSchema.extend({
  id: UUIDSchema,
});
export type OrganizationGetResponse = z.infer<
  typeof OrganizationGetResponseSchema
>;

export type OrganizationCreateRequest = Organization;

export type OrganizationUpdateRequest = OrganizationCreateRequest;

export type OrganizationCreateResponse = OrganizationGetResponse;

export type OrganizationUpdateResponse = OrganizationGetResponse;

export const OrganizationGetAllResponseSchema = PaginatedBundleSchema.extend({
  organizations: OrganizationGetResponseSchema.array(),
});
export type OrganizationGetAllResponse = z.infer<
  typeof OrganizationGetAllResponseSchema
>;

const OrganizationBaseSearchSchema = PaginationSchema.extend({
  name: z.string().optional().describe('The name of the organization'),
  address: z.string().optional().describe('Organization address'),
});

export const OrganizationSearchSchema = OrganizationBaseSearchSchema.extend({
  type: z
    .string()
    .optional()
    .describe('The type of organization, separated by a comma'),
});

export type OrganizationSearch = z.infer<typeof OrganizationSearchSchema>;

export type OrganizationCreateInput = z.infer<typeof OrganizationSchema>;

export const OrganizationGetAllInsuranceParamsSchema =
  OrganizationBaseSearchSchema;

export type OrganizationGetAllInsuranceParams = Omit<
  OrganizationSearch,
  'type'
>;

export const OrganizationGetAllInsurancesResponseSchema =
  PaginatedBundleSchema.extend({
    organizations: InsuranceOrganizationSchema.array(),
  });
export type OrganizationGetAllInsurancesResponse = z.infer<
  typeof OrganizationGetAllInsurancesResponseSchema
>;
